/**
 * Work Sample compliance flag reasons — org app_assignment metadata + UI helpers.
 * Mirrors utils/workSampleFlagReasons.js and api/constants/workSample.js FLAG_REASONS.
 */

export const FLAG_REASONS = Object.freeze({
  MISSING_STUDENT_NAME: 'missing_student_name',
  DATE_OUT_OF_PERIOD: 'date_out_of_period',
  MISSING_LEARNING_PERIOD: 'missing_learning_period',
  MISSING_SUBJECT: 'missing_subject',
  MISSING_FEEDBACK: 'missing_feedback',
  ILLEGIBLE_OR_NO_TRACK: 'illegible_or_no_track',
  COVER_SHEET_MISMATCH: 'cover_sheet_mismatch',
  PARENT_LOGS_MISALIGNED: 'parent_logs_misaligned',
  INSUFFICIENT_COUNT: 'insufficient_count',
  OTHER: 'other'
});

export const DEFAULT_FLAG_REASON_CATALOG = Object.freeze([
  { code: FLAG_REASONS.MISSING_STUDENT_NAME, label: 'Missing student legal name', active: true, sort: 1 },
  { code: FLAG_REASONS.DATE_OUT_OF_PERIOD, label: 'Date outside learning period', active: true, sort: 2 },
  { code: FLAG_REASONS.MISSING_LEARNING_PERIOD, label: 'Missing learning period', active: true, sort: 3 },
  { code: FLAG_REASONS.MISSING_SUBJECT, label: 'Missing subject area', active: true, sort: 4 },
  { code: FLAG_REASONS.MISSING_FEEDBACK, label: 'Missing teacher feedback / marks', active: true, sort: 5 },
  { code: FLAG_REASONS.ILLEGIBLE_OR_NO_TRACK, label: 'Illegible work or missing track', active: true, sort: 6 },
  { code: FLAG_REASONS.COVER_SHEET_MISMATCH, label: 'Cover sheet mismatch', active: true, sort: 7 },
  { code: FLAG_REASONS.PARENT_LOGS_MISALIGNED, label: 'Parent logs misaligned', active: true, sort: 8 },
  { code: FLAG_REASONS.INSUFFICIENT_COUNT, label: 'Insufficient sample count', active: true, sort: 9 },
  { code: FLAG_REASONS.OTHER, label: 'Other (describe below)', active: true, sort: 99 }
]);

/** @deprecated use DEFAULT_FLAG_REASON_CATALOG — kept for imports */
export const FLAG_REASON_OPTIONS = DEFAULT_FLAG_REASON_CATALOG.filter((entry) => entry.active !== false).map(
  (entry) => ({ value: entry.code, label: entry.label })
);

export function slugifyReasonCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeCatalogEntry(entry, index = 0) {
  if (!entry || typeof entry !== 'object') return null;
  const code = slugifyReasonCode(entry.code || entry.value || entry.key);
  const label = String(entry.label || entry.title || '').trim();
  if (!code || !label) return null;
  const sortRaw = Number(entry.sort);
  return {
    code,
    label,
    active: entry.active !== false,
    sort: Number.isFinite(sortRaw) ? sortRaw : index + 1
  };
}

function sortCatalog(entries) {
  return [...entries].sort((a, b) => {
    if (a.sort !== b.sort) return a.sort - b.sort;
    return a.label.localeCompare(b.label);
  });
}

export function parseFlagReasonsConfig(metadata) {
  const raw = metadata?.flag_reasons;
  if (!Array.isArray(raw) || !raw.length) return null;
  const parsed = raw.map(normalizeCatalogEntry).filter(Boolean);
  return parsed.length ? sortCatalog(parsed) : null;
}

export function resolveActiveFlagReasons(catalogOrMetadata) {
  const custom =
    catalogOrMetadata && typeof catalogOrMetadata === 'object' && !Array.isArray(catalogOrMetadata)
      ? parseFlagReasonsConfig(catalogOrMetadata)
      : null;
  const source = custom?.length
    ? custom
    : Array.isArray(catalogOrMetadata) && catalogOrMetadata.length
      ? catalogOrMetadata
      : DEFAULT_FLAG_REASON_CATALOG;
  return sortCatalog(source.filter((entry) => entry.active !== false));
}

export function serializeFlagReasonsConfig(entries) {
  if (!Array.isArray(entries) || !entries.length) return [];
  return sortCatalog(
    entries
      .map(normalizeCatalogEntry)
      .filter(Boolean)
      .map((entry, index) => ({
        code: entry.code,
        label: entry.label,
        active: entry.active !== false,
        sort: entry.sort ?? index + 1
      }))
  );
}

export function collectCommentReasons(metadata) {
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  if (Array.isArray(meta.reasons) && meta.reasons.length) {
    return meta.reasons.map((code) => slugifyReasonCode(code)).filter(Boolean);
  }
  const legacy = slugifyReasonCode(meta.reason);
  return legacy ? [legacy] : [];
}

function buildLabelLookup(catalog) {
  return Object.fromEntries((catalog || []).map((entry) => [entry.code, entry.label]));
}

export function formatFlagReason(value, catalog = null) {
  const key = slugifyReasonCode(value);
  if (!key) return '';
  const lookup = buildLabelLookup(catalog || DEFAULT_FLAG_REASON_CATALOG);
  return lookup[key] || key.replace(/_/g, ' ');
}

export function formatCommentReasonLabels(metadata, catalog = null) {
  const activeCatalog = resolveActiveFlagReasons(catalog || DEFAULT_FLAG_REASON_CATALOG);
  const lookup = buildLabelLookup(activeCatalog);
  const labels = Array.isArray(metadata?.reason_labels) ? metadata.reason_labels : [];
  return collectCommentReasons(metadata).map((code, index) => {
    if (labels[index]) return String(labels[index]).trim();
    return lookup[code] || code.replace(/_/g, ' ');
  });
}

export function createEmptyFlagReasonEntry(sort = 99) {
  return { code: '', label: '', active: true, sort };
}
